import type { PadButton, SystemId } from '../emulator/types';
import { SYSTEMS } from '../emulator/systems';

/**
 * バーチャルパッドのゾーン定義。
 *
 * 位置とサイズを 1 か所のテーブルに集約してある。こうしておくと
 * 「既定の配置」と「ユーザーが編集した配置」を同じ計算で扱えるので、
 * レイアウト編集機能が描画側の分岐を増やさずに済む。
 *
 * 座標は中心の比率 (0..1)。
 * 縦持ち: パッド枠（ChassisDecor）内の比率。枠の外には出さない。
 * 横持ち: 画面全体に対する比率。共通の汎用配置。
 *
 * 縦持ちの size は枠幅に対する％。横持ちの size は vmin。
 */

/** 縦持ちパッド枠。VirtualPad の ChassisDecor と同じ inset。 */
export const PORTRAIT_CHASSIS = {
  /** 画面左からの枠左端 */
  left: 0.04,
  /** 枠の幅（画面比） */
  width: 0.92,
  /** 画面上からの枠上端 */
  top: 0.5,
  /** 枠の高さ目安（画面比。下端は safe-area で少し変わる） */
  height: 0.485,
} as const;

/** 縦持ちパッド枠の Tailwind 用クラス（位置・サイズの単一ソース） */
export const PORTRAIT_CHASSIS_CLASS =
  'absolute inset-x-[4%] bottom-[max(1.5%,env(safe-area-inset-bottom,0px))] top-[50%]';


export type ZoneKind = 'button' | 'dpad' | 'stick';

/**
 * 方向入力の見た目と操作方法。
 *
 * dpad            … 従来の十字キー。押した場所で 8 方向を決める
 * stick           … 固定位置のスティック。倒した向きが方向キーになる
 * floating-stick  … 触れた場所が中心になるスティック
 *
 * 十字キーは中心を指で探す必要があり、画面を見ずに操作すると外しやすい。
 * スティックはノブが指に追従するので、いま何方向に入っているかが目で分かる。
 * フローティングはさらに「中心を探す」動作自体が要らなくなる。
 */
export type DirectionalStyle = 'dpad' | 'stick' | 'floating-stick';

export const DIRECTIONAL_STYLES: Array<{
  id: DirectionalStyle;
  name: string;
  description: string;
}> = [
  { id: 'dpad', name: '十字キー', description: '実機に近い形。押した場所で 8 方向が決まる' },
  {
    id: 'stick',
    name: 'スティック',
    description: '倒した向きが方向キーになる。ノブが指に追従するので入力が目で分かる',
  },
  {
    id: 'floating-stick',
    name: 'フローティングスティック',
    description: '触れた場所が中心になる。画面を見ずに操作しても中心を外さない',
  },
];

export interface ZoneDef {
  id: string;
  kind: ZoneKind;
  /** kind === 'button' のとき対応するボタン */
  button?: PadButton;
  label?: string;
  /**
   * 基準サイズ。
   * 縦持ち: 枠幅％（portraitSize があればそちら優先）。
   * 横持ち: vmin。
   */
  size: number;
  /** 縦持ち専用サイズ（枠幅％）。機種別スキンが横持ちの size を汚さないために分離。 */
  portraitSize?: number;
  /** 縦長のボタン（肩ボタン）の縦横比。省略時は正方形。 */
  heightRatio?: number;
  /** 縦持ち専用の縦横比 */
  portraitHeightRatio?: number;
  /** 当たり判定を見た目より広げる比率 */
  slop: number;
  /** 既定位置（中心、0..1）。縦持ち／横持ちで別に持つ。 */
  portrait: { x: number; y: number };
  landscape: { x: number; y: number };
  /** kind === 'stick': 倒した方向を十字キーとしても出す */
  asDpad?: boolean;
  /** kind === 'stick': 触れた場所を中心にする */
  floating?: boolean;
}

/** ユーザーが編集した 1 ゾーンぶんの配置 */
export interface ZoneLayout {
  x: number;
  y: number;
  /** 個別の大きさ倍率 */
  scale: number;
}

export type PadLayout = Record<string, ZoneLayout>;

export interface PadLayouts {
  portrait: PadLayout;
  landscape: PadLayout;
}

export const EMPTY_LAYOUTS: PadLayouts = { portrait: {}, landscape: {} };

type PortraitOverride = Partial<Pick<ZoneDef, 'size' | 'heightRatio' | 'slop' | 'label'>> & {
  portrait: { x: number; y: number };
};

/* -------------------------------------------------------------------------- */

const ALL_ZONES: ZoneDef[] = [
  {
    id: 'dpad',
    kind: 'dpad',
    // 縦: portraitSize（機種別）/ 横: vmin。画面中央のゲーム面を避ける。
    size: 24,
    slop: 1.1,
    portrait: { x: 0.24, y: 0.48 },
    landscape: { x: 0.12, y: 0.52 },
  },
  {
    id: 'stick-left',
    kind: 'stick',
    size: 16,
    slop: 1.05,
    portrait: { x: 0.38, y: 0.72 },
    // 十字より下（DualShock 寄せ）。L 帯と被らないよう左下へ。
    landscape: { x: 0.13, y: 0.80 },
  },

  // フェイスボタン（菱形。横持ちは右端・ゲーム面の外）
  {
    id: 'btn-x',
    kind: 'button',
    button: 'X',
    label: 'X',
    size: 12,
    slop: 1.18,
    portrait: { x: 0.74, y: 0.36 },
    landscape: { x: 0.88, y: 0.48 },
  },
  {
    id: 'btn-a',
    kind: 'button',
    button: 'A',
    label: 'A',
    size: 12,
    slop: 1.18,
    portrait: { x: 0.84, y: 0.48 },
    landscape: { x: 0.95, y: 0.60 },
  },
  {
    id: 'btn-b',
    kind: 'button',
    button: 'B',
    label: 'B',
    size: 12,
    slop: 1.18,
    portrait: { x: 0.74, y: 0.60 },
    landscape: { x: 0.88, y: 0.72 },
  },
  {
    id: 'btn-y',
    kind: 'button',
    button: 'Y',
    label: 'Y',
    size: 12,
    slop: 1.18,
    portrait: { x: 0.64, y: 0.48 },
    landscape: { x: 0.81, y: 0.60 },
  },

  // 肩ボタン。横持ち: L2/R2 が上段、L1/R1 が下段（実機 DualShock 寄せ）
  {
    id: 'btn-l',
    kind: 'button',
    button: 'L',
    label: 'L',
    size: 16,
    portraitSize: 20,
    heightRatio: 0.38,
    portraitHeightRatio: 0.34,
    slop: 1.15,
    portrait: { x: 0.17, y: 0.22 },
    landscape: { x: 0.07, y: 0.30 },
  },
  {
    id: 'btn-r',
    kind: 'button',
    button: 'R',
    label: 'R',
    size: 16,
    portraitSize: 20,
    heightRatio: 0.38,
    portraitHeightRatio: 0.34,
    slop: 1.15,
    portrait: { x: 0.83, y: 0.22 },
    landscape: { x: 0.93, y: 0.30 },
  },
  {
    id: 'btn-l2',
    kind: 'button',
    button: 'L2',
    label: 'L2',
    size: 16,
    portraitSize: 20,
    heightRatio: 0.38,
    portraitHeightRatio: 0.34,
    slop: 1.15,
    portrait: { x: 0.17, y: 0.11 },
    landscape: { x: 0.07, y: 0.16 },
  },
  {
    id: 'btn-r2',
    kind: 'button',
    button: 'R2',
    label: 'R2',
    size: 16,
    portraitSize: 20,
    heightRatio: 0.38,
    portraitHeightRatio: 0.34,
    slop: 1.15,
    portrait: { x: 0.83, y: 0.11 },
    landscape: { x: 0.93, y: 0.16 },
  },

  // START / SELECT
  {
    id: 'btn-select',
    kind: 'button',
    button: 'SELECT',
    label: 'SELECT',
    size: 14,
    heightRatio: 0.40,
    slop: 1.2,
    portrait: { x: 0.38, y: 0.86 },
    landscape: { x: 0.42, y: 0.92 },
  },
  {
    id: 'btn-start',
    kind: 'button',
    button: 'START',
    label: 'START',
    size: 14,
    heightRatio: 0.40,
    slop: 1.2,
    portrait: { x: 0.62, y: 0.86 },
    landscape: { x: 0.58, y: 0.92 },
  },
];

/**
 * 縦持ち専用の実機風配置（商標ロゴは使わず配置・色で寄せる）。
 *
 * 座標・size はパッド枠内基準（枠幅％）。角丸を見越し中心は概ね 0.18–0.82。
 * 縦リズム: 速度HUD(上中央) / 肩(0.12–0.24) → 主操作(0.36–0.60) → START/SELECT(0.86)。
 */
const PORTRAIT_HARDWARE: Record<SystemId, Record<string, PortraitOverride>> = {
  gb: {
    dpad: { size: 34, portrait: { x: 0.24, y: 0.46 } },
    'btn-b': { size: 15, label: 'B', portrait: { x: 0.68, y: 0.54 } },
    'btn-a': { size: 15, label: 'A', portrait: { x: 0.82, y: 0.40 } },
    'btn-select': {
      size: 14,
      heightRatio: 0.42,
      label: 'SELECT',
      portrait: { x: 0.38, y: 0.86 },
    },
    'btn-start': {
      size: 14,
      heightRatio: 0.42,
      label: 'START',
      portrait: { x: 0.56, y: 0.86 },
    },
  },
  gba: {
    // AGB-001: 十字と A/B が横並び、L/R は上肩。START/SELECT は中央下。
    dpad: { size: 32, portrait: { x: 0.24, y: 0.48 } },
    'btn-b': { size: 14, label: 'B', portrait: { x: 0.68, y: 0.54 } },
    'btn-a': { size: 14, label: 'A', portrait: { x: 0.82, y: 0.40 } },
    'btn-l': { size: 20, heightRatio: 0.32, label: 'L', portrait: { x: 0.17, y: 0.18 } },
    'btn-r': { size: 20, heightRatio: 0.32, label: 'R', portrait: { x: 0.83, y: 0.18 } },
    'btn-select': {
      size: 14,
      heightRatio: 0.42,
      label: 'SELECT',
      portrait: { x: 0.38, y: 0.86 },
    },
    'btn-start': {
      size: 14,
      heightRatio: 0.42,
      label: 'START',
      portrait: { x: 0.56, y: 0.86 },
    },
  },
  nes: {
    dpad: { size: 32, portrait: { x: 0.24, y: 0.48 } },
    'btn-b': { size: 14, label: 'B', portrait: { x: 0.68, y: 0.48 } },
    'btn-a': { size: 14, label: 'A', portrait: { x: 0.82, y: 0.48 } },
    'btn-select': {
      size: 15,
      heightRatio: 0.36,
      label: 'SELECT',
      portrait: { x: 0.38, y: 0.86 },
    },
    'btn-start': {
      size: 15,
      heightRatio: 0.36,
      label: 'START',
      portrait: { x: 0.58, y: 0.86 },
    },
  },
  snes: {
    dpad: { size: 30, portrait: { x: 0.24, y: 0.50 } },
    'btn-x': { size: 13, label: 'X', portrait: { x: 0.74, y: 0.36 } },
    'btn-a': { size: 13, label: 'A', portrait: { x: 0.84, y: 0.48 } },
    'btn-b': { size: 13, label: 'B', portrait: { x: 0.74, y: 0.60 } },
    'btn-y': { size: 13, label: 'Y', portrait: { x: 0.64, y: 0.48 } },
    'btn-l': { size: 20, heightRatio: 0.32, label: 'L', portrait: { x: 0.17, y: 0.18 } },
    'btn-r': { size: 20, heightRatio: 0.32, label: 'R', portrait: { x: 0.83, y: 0.18 } },
    'btn-select': {
      size: 14,
      heightRatio: 0.40,
      label: 'SELECT',
      portrait: { x: 0.38, y: 0.86 },
    },
    'btn-start': {
      size: 14,
      heightRatio: 0.40,
      label: 'START',
      portrait: { x: 0.56, y: 0.86 },
    },
  },
  psx: {
    dpad: { size: 28, portrait: { x: 0.22, y: 0.46 } },
    'stick-left': { size: 18, portrait: { x: 0.38, y: 0.70 } },
    'btn-x': { size: 13, label: '△', portrait: { x: 0.74, y: 0.36 } },
    'btn-a': { size: 13, label: '○', portrait: { x: 0.84, y: 0.48 } },
    'btn-b': { size: 13, label: '×', portrait: { x: 0.74, y: 0.60 } },
    'btn-y': { size: 13, label: '□', portrait: { x: 0.64, y: 0.48 } },
    'btn-l': { size: 18, heightRatio: 0.34, label: 'L1', portrait: { x: 0.17, y: 0.24 } },
    'btn-r': { size: 18, heightRatio: 0.34, label: 'R1', portrait: { x: 0.83, y: 0.24 } },
    'btn-l2': { size: 18, heightRatio: 0.34, label: 'L2', portrait: { x: 0.17, y: 0.11 } },
    'btn-r2': { size: 18, heightRatio: 0.34, label: 'R2', portrait: { x: 0.83, y: 0.11 } },
    'btn-select': {
      size: 13,
      heightRatio: 0.42,
      label: 'SELECT',
      portrait: { x: 0.40, y: 0.86 },
    },
    'btn-start': {
      size: 13,
      heightRatio: 0.42,
      label: 'START',
      portrait: { x: 0.60, y: 0.86 },
    },
  },
  ws: {
    dpad: { size: 32, portrait: { x: 0.24, y: 0.46 } },
    'btn-b': { size: 14, label: 'B', portrait: { x: 0.68, y: 0.54 } },
    'btn-a': { size: 14, label: 'A', portrait: { x: 0.82, y: 0.40 } },
    'btn-select': {
      size: 14,
      heightRatio: 0.42,
      label: 'SELECT',
      portrait: { x: 0.38, y: 0.86 },
    },
    'btn-start': {
      size: 14,
      heightRatio: 0.42,
      label: 'START',
      portrait: { x: 0.56, y: 0.86 },
    },
  },
  psp: {
    dpad: { size: 26, portrait: { x: 0.22, y: 0.46 } },
    'stick-left': { size: 17, portrait: { x: 0.38, y: 0.70 } },
    'btn-x': { size: 12, label: '△', portrait: { x: 0.74, y: 0.36 } },
    'btn-a': { size: 12, label: '○', portrait: { x: 0.84, y: 0.48 } },
    'btn-b': { size: 12, label: '×', portrait: { x: 0.74, y: 0.60 } },
    'btn-y': { size: 12, label: '□', portrait: { x: 0.64, y: 0.48 } },
    'btn-l': { size: 20, heightRatio: 0.32, label: 'L', portrait: { x: 0.17, y: 0.18 } },
    'btn-r': { size: 20, heightRatio: 0.32, label: 'R', portrait: { x: 0.83, y: 0.18 } },
    'btn-select': {
      size: 13,
      heightRatio: 0.42,
      label: 'SELECT',
      portrait: { x: 0.40, y: 0.86 },
    },
    'btn-start': {
      size: 13,
      heightRatio: 0.42,
      label: 'START',
      portrait: { x: 0.60, y: 0.86 },
    },
  },
};

/**
 * その機種・その構成で表示するゾーンを返す。
 *
 * フェイスボタンが 2 つ（GB/NES）のときは X/Y を出さず、
 * 縦持ちは機種別の実機風配置を、横持ちは従来の汎用配置を使う。
 */
export function zonesFor(
  system: SystemId,
  opts: { analog?: boolean; directionalStyle?: DirectionalStyle } = {},
): ZoneDef[] {
  const buttons = SYSTEMS[system].buttons;
  const twoButton = !buttons.includes('X');
  const portraitSkin = PORTRAIT_HARDWARE[system];
  const style = opts.directionalStyle ?? 'dpad';

  const zones = ALL_ZONES.filter((z) => {
    if (z.id === 'dpad') return true;
    if (z.kind === 'stick') return opts.analog === true;
    return z.button ? buttons.includes(z.button) : false;
  }).map((z) => {
    let next = z;

    // 横持ち: 2 ボタン機は A/B を寄せる（従来どおり）
    if (twoButton) {
      if (z.id === 'btn-a') {
        next = {
          ...next,
          size: 15,
          landscape: { x: 0.93, y: 0.56 },
        };
      } else if (z.id === 'btn-b') {
        next = {
          ...next,
          size: 15,
          landscape: { x: 0.82, y: 0.72 },
        };
      }
    }

    const override = portraitSkin[next.id];
    if (!override) return next;
    // size / heightRatio は縦持ち専用に載せ、横持ちの vmin を汚さない
    return {
      ...next,
      ...(override.label !== undefined ? { label: override.label } : {}),
      portrait: override.portrait,
      landscape: next.landscape,
      ...(override.size !== undefined ? { portraitSize: override.size } : {}),
      ...(override.heightRatio !== undefined
        ? { portraitHeightRatio: override.heightRatio }
        : {}),
    };
  });

  // 方向入力のゾーンだけ、設定に応じて十字キー／スティックへ差し替える。
  // 機種別スキンを当てたあとに実行するのは、フローティングの位置と大きさで
  // スキン側の指定を上書きしたいため。
  //
  // ID は 'dpad' のまま据え置く。変えると保存済みのパッド配置が
  // 引き継がれず、編集した人の設定が黙って消える。
  if (style === 'dpad') return zones;
  return zones.map((z) => {
    if (z.id !== 'dpad') return z;
    return {
      ...z,
      kind: 'stick' as const,
      asDpad: true,
      floating: style === 'floating-stick',
      // フローティングは触れた場所が中心なので、受け付ける範囲を広く取る
      ...(style === 'floating-stick'
        ? {
            size: 28,
            portraitSize: 36,
            slop: 1,
            portrait: { x: 0.26, y: 0.48 },
            landscape: { x: 0.13, y: 0.55 },
          }
        : {}),
    };
  });
}

/** 既定位置と編集済みの配置をまとめて、実際に使う配置を返す。 */
export function resolveLayout(
  zone: ZoneDef,
  orientation: 'portrait' | 'landscape',
  layout: PadLayout | undefined,
): ZoneLayout {
  const custom = layout?.[zone.id];
  if (custom) return custom;
  const base = orientation === 'portrait' ? zone.portrait : zone.landscape;
  return { x: base.x, y: base.y, scale: 1 };
}

/**
 * 旧・画面座標系の縦持ちカスタム配置を、枠内座標へ変換する。
 * 肩ボタンが枠上端付近（y≈0.1）なら新形式とみなして触らない。
 */
export function migratePortraitLayout(layout: PadLayout): PadLayout {
  const entries = Object.entries(layout);
  if (entries.length === 0) return layout;
  const looksLikeScreenSpace = entries.every(([, z]) => z.y >= 0.45);
  if (!looksLikeScreenSpace) return layout;
  const { left, width, top, height } = PORTRAIT_CHASSIS;
  return Object.fromEntries(
    entries.map(([id, z]) => [
      id,
      {
        ...z,
        x: Math.min(0.97, Math.max(0.03, (z.x - left) / width)),
        y: Math.min(0.97, Math.max(0.03, (z.y - top) / height)),
      },
    ]),
  );
}

/** 枠／画面の外へ出ないよう中心座標を丸める。 */
export function clampPosition(x: number, y: number): { x: number; y: number } {
  return {
    x: Math.min(0.97, Math.max(0.03, x)),
    y: Math.min(0.97, Math.max(0.03, y)),
  };
}

export const MIN_ZONE_SCALE = 0.6;
export const MAX_ZONE_SCALE = 1.8;
